import { useRouter } from 'vue-router/composables'
import store from '@/store'
import { FindShopMember } from '@/api/auth'
import { FindMember, CheckUserPhone } from '@/api/member'

/**
 * QR Code 解析和會員查詢的 Composable
 *
 * 支援以下格式:
 * 1. bob:member:{memberId} - BOB 會員 QR Code 格式
 * 2. 09xxxxxxxx - 10 位數手機號碼格式
 * 3. UUID 格式的會員 ID
 *
 * @returns {Object} 包含 parseScannedContent、parseAndNavigate、parseAndCallback 方法的物件
 */
export function useQrCodeParser () {
  const router = useRouter()

  /**
   * 導航到會員資料頁面
   *
   * @param {string} memberId - 會員 ID
   */
  const goProfile = (memberId) => {
    router.push({
      name: 'MemberProfile',
      params: { id: memberId },
    })
  }

  /**
   * 解析掃描內容並返回結構化結果
   *
   * @param {string} scannedContent - 掃描到的內容
   * @returns {Promise<Object>} 結果物件 { status, member, user, message, phone }
   * status: 'success' | 'sync_required' | 'error'
   */
  const parseScannedContent = async (scannedContent) => {
    const shopId = store.getters.shop

    if (!scannedContent || !shopId) {
      return { status: 'error', message: '無效的掃描內容' }
    }

    // 1. 支援手機號碼格式（09 開頭，總共 10 碼）
    if (/^09\d{8}$/.test(scannedContent)) {
      const phone = scannedContent
      try {
        const [res, err] = await CheckUserPhone({ shopId, phone })

        if (err) {
          return { status: 'error', message: err }
        }

        // 4-a. 存在 user, 且存在會員
        if (res.member) {
          return { status: 'success', member: res.member, user: res.user }
        }

        // 4-b. 存在 user, 但不存在會員 (需同步)
        if (res.user) {
          return { status: 'sync_required', user: res.user, phone }
        }

        // 4-c. 不存在 user, 也不存在會員
        return { status: 'error', message: '查無會員' }
      } catch (error) {
        console.error('CheckUserPhone error:', error)
        return { status: 'error', message: '查詢會員時發生錯誤' }
      }
    }

    // 2. 支援 bob:member:{id} 格式
    const parts = scannedContent.split(':')
    if (parts[0] === 'bob' && parts[1] === 'member') {
      const memberId = parts[2]

      if (!memberId) {
        return { status: 'error', message: '無效的會員 QR Code' }
      }

      const [res, err] = await FindShopMember({ shopId, id: memberId })

      if (err) {
        return { status: 'error', message: '非店家會員' }
      }

      if (res) {
        return { status: 'success', member: res }
      }

      return { status: 'error', message: '查無會員' }
    }

    // 3. 支援 UUID 格式的會員 ID（兜底邏輯）
    const uuidMatch = scannedContent.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
    if (uuidMatch) {
      const memberId = uuidMatch[0]

      try {
        const res = await FindMember({ shopId, id: memberId })
        if (res && res.id) {
          return { status: 'success', member: res }
        }
        return { status: 'error', message: '查無此會員' }
      } catch (error) {
        return { status: 'error', message: '查無此會員' }
      }
    }

    // 4. 不可識別的格式
    return { status: 'error', message: '不可用的 QRCode 或資料格式' }
  }

  /**
   * 解析掃描內容並導航到對應的會員頁面 (兼容舊版 API)
   *
   * @param {string} scannedContent - 掃描到的內容
   * @returns {Promise<boolean>} 返回 true 表示成功找到會員並導航，false 表示失敗
   */
  const parseAndNavigate = async (scannedContent) => {
    const result = await parseScannedContent(scannedContent)

    if (result.status === 'success') {
      window.$message.success('解析會員成功')
      goProfile(result.member.id)
      return true
    } else if (result.status === 'sync_required') {
      // 舊版行為不支援同步對話框，顯示提示
      window.$message.error('會員資料需同步，請使用掃描槍功能或聯絡管理員')
      return false
    } else {
      window.$message.error(result.message)
      return false
    }
  }

  /**
   * 解析掃描內容並執行自定義回調函數 (兼容舊版 API)
   *
   * @param {string} scannedContent - 掃描到的內容
   * @param {Function} callback - 成功解析後的回調函數，接收會員資料作為參數
   * @returns {Promise<boolean>} 返回 true 表示成功找到會員並執行回調，false 表示失敗
   */
  const parseAndCallback = async (scannedContent, callback) => {
    const result = await parseScannedContent(scannedContent)
    console.log('parseAndCallback result:', result)

    if (result.status === 'success') {
      window.$message.success('解析會員成功')
      if (typeof callback === 'function') {
        callback(result.member, result.user)
      }
      return true
    } else {
      window.$message.error(result.message || '解析失敗')
      return false
    }
  }

  return {
    parseScannedContent,
    parseAndNavigate,
    parseAndCallback,
  }
}
